import { Controller } from "@hotwired/stimulus"

// add the JS
import TomSelect from 'tom-select'

// Connects to data-controller="tomselect"
export default class extends Controller {
  static values = {
    option: String
  }
  // Triggered when the Stimulus controller is connected to the DOM.
  connect() {
    this.initializeTomSelect();
  }

  // Triggered when the Stimulus controller is removed from the DOM.
  disconnect() {
    this.destroyTomSelect();
  }

  // Initialize the TomSelect dropdown with the desired configurations.
  initializeTomSelect() {
    // Return early if no element is associated with the controller.
    if (!this.element) return;

    const renderIcon = (type) => {
      if (type === "institution") {
        return '<i class="bi bi-building image" style="font-size:18px; color:#adb5bd; background:#f3f4f6; width:32px; height:32px; display:flex; align-items:center; justify-content:center; border-radius:50%;"></i>';
      }
      if (type === "lab") {
        return '<i class="bi bi-flask image" style="font-size:18px; color:#adb5bd; background:#f3f4f6; width:32px; height:32px; display:flex; align-items:center; justify-content:center; border-radius:50%;"></i>';
      }
      if (type === "project") {
        return '<i class="bi bi-kanban image" style="font-size:18px; color:#adb5bd; background:#f3f4f6; width:32px; height:32px; display:flex; align-items:center; justify-content:center; border-radius:50%;"></i>';
      }
      return '';
    };

    // Define custom rendering for dropdown options.
    // see: https://tom-select.js.org/examples/customization/
    const renderOption = (data, escape) => {
      let imgHtml;
      if (data.src && data.src.startsWith("icon:")) {
        imgHtml = renderIcon(data.src.replace("icon:", ""));
      } else {
        imgHtml = '<img class="image" src="' + escape(data.src) + '">';
      }
      if (data.option == 'institutions') {
        return '<div class="container">' +
          imgHtml +
          '<div class="text-container">' +
          '<span class="title">' + (data.acronym === undefined ? escape(data.name) : `${escape(data.name)} (${escape(data.acronym)})`) + '</span>' +
          '</div>' +
          '</div>';
      } else if (data.option == 'researchers' || data.option == 'researcher') {
        let details = [];
        if (data.email) details.push(escape(data.email));
        if (data.orcid) details.push('ORCID: ' + escape(data.orcid));
        return '<div class="container">' +
          '<img class="image" src="' + escape(data.src) + '">' +
          '<div class="text-container">' +
          '<span class="title">' + escape(data.name) + '</span>' +
          (details.length > 0 ? '<span class="url">' + details.join(' / ') + '</span>' : '') +
          '</div>' +
          '</div>';
      } else if (data.option == 'project' || data.option == 'projects') {
        return '<div class="container">' +
          imgHtml +
          '<div class="text-container">' +
          '<span class="title">' + escape(data.name) + ' <small class="text-muted"> - ' + escape(data.number) + '</small></span>' +
          '<span class="url">' + escape(data.title) + '</span>' +
          '</div>' +
          '</div>';
      }
      else if (data.option == 'research_lines' || data.option == 'research_line') {
        return '<div class="container">' +
          '<div class="text-container">' +
          '<span class="title"><b>' + escape(data.acronym) + '</b> - ' + escape(data.name) + '</span>' +
          '</div>' +
          '</div>';
      }
      else if (data.option == 'labs') {
        return '<div class="container">' +
          imgHtml +
          '<div class="text-container">' +
          '<span class="title">' + escape(data.acronym) + ' - ' + escape(data.name) + '</span>' +
          '</div>' +
          '</div>';
      }
    };

    // Define custom rendering for selected items.
    const renderItem = (item, escape) => {
      let imgHtml;
      if (item.src && item.src.startsWith("icon:")) {
        imgHtml = renderIcon(item.src.replace("icon:", ""));
      } else {
        imgHtml = `<img class="image" src="${escape(item.src)}">`;
      }
      if (item.option === 'labs' || item.option === 'institutions') {
        return `<div>${imgHtml}${escape(item.name)}</div>`;
      } else if (item.option === 'researchers' || item.option === 'researcher') {
        return `<div><img class="image" src="${escape(item.src)}">${escape(item.name)}</div>`;
      } else if (item.option === 'project' || item.option === 'projects') {
        return `<div>${imgHtml}${escape(item.name)}</div>`;
      } else if (item.option === 'research_lines') {
        return `<div>${escape(item.name)}</div>`;
      }
      return `<div>${escape(item.name)}</div>`;
    };

    let selectOptions = {
      plugins: {
        remove_button: {
          title: 'Remove this item',
        },
        checkbox_options: {
          'checkedClassNames': ['ts-checked'],
          'uncheckedClassNames': ['ts-unchecked'],
        }
      },
      valueField: 'id',
      labelField: 'name',
      searchField: ['name'],
      maxItems: null,
      highlight: true,
      sortField: { field: "name", direction: "asc" },
      render: {
        option: renderOption,
        item: renderItem
      }
    };

    if (this.optionValue === "project") {
      delete selectOptions.plugins.checkbox_options;
      delete selectOptions.plugins.remove_button;
      selectOptions.maxItems = 1;
      selectOptions.searchField = ['name', 'title', 'number'];
    }
    else if (this.optionValue === "projects") {
      selectOptions.searchField = ['name', 'title', 'number'];
    }
    else if (this.optionValue === "researcher") {
      delete selectOptions.plugins.checkbox_options;
      delete selectOptions.plugins.remove_button;
      selectOptions.maxItems = 1;
      selectOptions.searchField = ['name', 'orcid', 'email'];
    }
    else if (this.optionValue === "institutions") {
      selectOptions.searchField = ['name', 'acronym', 'institutiontype'];
    }
    else if (this.optionValue === "researchers") {
      selectOptions.searchField = ['name', 'orcid', 'email'];
    }
    else if (this.optionValue === "labs") {
      selectOptions.searchField = ['name', 'acronym'];
    }
    else if (this.optionValue === "research_line") {
      delete selectOptions.plugins.checkbox_options;
      delete selectOptions.plugins.remove_button;
      selectOptions.maxItems = 1;
      selectOptions.searchField = ['name', 'acronym'];
    }
    else if (this.optionValue === "research_lines") {
      selectOptions.searchField = ['name', 'acronym'];
    }

    console.log(this.maxitemsValue);
    // Create a new TomSelect instance with the specified configuration.
    // see: https://tom-select.js.org/docs/
    // value, label, search, placeholder, etc can all be passed as static values instead of hard-coded.
    this.select = new TomSelect(this.element, selectOptions);
  }

  // Cleanup: Destroy the TomSelect instance when the controller is disconnected.
  destroyTomSelect() {
    if (this.select) {
      this.select.destroy();
    }
  }
}

//NOTE: might be useful if we want to use AJAX to get data instead - https://gist.github.com/tabishiqbal/dc78239aa5b81b257db0633ace75ecc0